import React, { useState } from 'react';

const ActionCenterPanel = ({
  priorities,
  processPrioritiesData,
  filters,
  salesEscalations,
  engineerEscalations,
  inventoryTasks,
  getSalesEscalationsCount,
  getEngineerEscalationsCount,
  getInventoryEscalationsCount,
  onPriorityClick,
  onApprovalClick,
  onEscalationClick,
  panelHeight
}) => {
  const [actionCenterTab, setActionCenterTab] = useState('priorities');
  const hasProjectFilter = Boolean(filters?.projectID && filters.projectID !== '');
  const processedData = priorities
    ? processPrioritiesData(priorities?.data)
    : { priorities: [], approvals: [] };
  const prioritiesTabCount = processedData.priorities.reduce(
    (sum, p) => sum + (p.type === 'priority' ? p.count || 0 : 0),
    0
  );
  const approvalsTabCount = processedData.approvals.reduce(
    (sum, a) => sum + (a.type === 'approval' ? a.count || 0 : 0),
    0
  );
  const escalationsTabCount =
    getSalesEscalationsCount(salesEscalations?.data) +
    getEngineerEscalationsCount(engineerEscalations?.data) +
    getInventoryEscalationsCount(inventoryTasks);
  const hasEscalations = escalationsTabCount > 0;

  return (
    <div
      className="flex h-full w-full min-w-0 flex-col bg-gray-100 shadow"
      style={{ height: panelHeight > 0 ? `${panelHeight}px` : 'calc(100vh - 6rem)' }}>
      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-white p-4 shadow-lg">
        <div className="shrink-0">
        <h3 className="text-lg font-semibold text-[#1E4A53] mb-4">Action Center</h3>
        <div className="mb-4 grid w-full min-w-0 grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => setActionCenterTab('priorities')}
            className={`min-w-0 rounded-lg px-1 py-2 text-center text-xs font-semibold leading-tight whitespace-nowrap sm:text-sm ${
              actionCenterTab === 'priorities'
                ? 'bg-white text-[#0A4A5C] shadow'
                : 'text-gray-600 hover:bg-white/70'
            }`}>
            Priorities ({prioritiesTabCount})
          </button>
          <button
            type="button"
            onClick={() => setActionCenterTab('approvals')}
            className={`min-w-0 rounded-lg px-1 py-2 text-center text-xs font-semibold leading-tight whitespace-nowrap sm:text-sm ${
              actionCenterTab === 'approvals'
                ? 'bg-white text-[#0A4A5C] shadow'
                : 'text-gray-600 hover:bg-white/70'
            }`}>
            Approvals ({approvalsTabCount})
          </button>
          <button
            type="button"
            onClick={() => setActionCenterTab('escalations')}
            className={`min-w-0 rounded-lg px-1 py-2 text-center text-xs font-semibold leading-tight whitespace-nowrap sm:text-sm ${
              actionCenterTab === 'escalations'
                ? 'bg-white text-[#0A4A5C] shadow'
                : 'text-gray-600 hover:bg-white/70'
            }`}>
            Escalations ({escalationsTabCount})
          </button>
        </div>
        </div>

        <div className="flex-1 overflow-y-auto pr-1">
        {actionCenterTab === 'priorities' && (
          <div className="mb-6 p-1">
            {processedData.priorities.length > 0 ? (
              processedData.priorities.map((item, index) => {
                if (item.type !== 'priority') return null;

                const displayLabel = item.priorityTitle || item.priorityType;
                const projectId = item.items?.[0]?.project_id || filters.projectID;
                const projectName = item.items?.[0]?.project?.name;

                return (
                  <div
                    key={`priority-${item.priorityTitle || item.priorityType}-${index}-${projectId}`}
                    className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                    onClick={() => onPriorityClick(item, projectId)}>
                    <div className="flex flex-col gap-1 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold">
                          {displayLabel.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-gray-800">{displayLabel}</span>
                      </div>
                      <div className="flex items-center justify-start gap-1">
                        {item.items?.[0]?.tag &&
                          (() => {
                            const tag = item.items[0].tag;
                            let tagClasses =
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';

                            if (tag === 'Operations') {
                              tagClasses += ' bg-blue-100 text-blue-700 border border-blue-300';
                            } else if (tag === 'Sales') {
                              tagClasses += ' bg-green-100 text-green-700 border border-green-300';
                            } else if (tag === 'Management') {
                              tagClasses +=
                                ' bg-purple-100 text-purple-700 border border-purple-300';
                            } else {
                              tagClasses += ' bg-gray-100 text-gray-700 border border-gray-300';
                            }

                            return (
                              <div className="ml-8 mt-1">
                                <span className={tagClasses}>{tag}</span>
                              </div>
                            );
                          })()}
                        {!hasProjectFilter && projectName && (
                          <div className=" mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                              {projectName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 px-3">
                <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">No priorities pending</p>
                <p className="text-xs text-gray-500 mt-1">Nothing requires your action right now.</p>
              </div>
            )}
          </div>
        )}

        {actionCenterTab === 'approvals' && (
          <div className="mb-6 p-1">
            {processedData.approvals.length > 0 ? (
              processedData.approvals.map((item, index) => {
                if (item.type !== 'approval') return null;

                let displayLabel;
                if (hasProjectFilter) {
                  if (item.priorityType === 'Deal Approval') {
                    displayLabel = 'Approve Closed Deal';
                  } else if (item.priorityType === 'Approve UC') {
                    displayLabel = 'Approve Under Consideration';
                  } else if (item.priorityType === 'Approve CCR') {
                    displayLabel = 'Approve Customer Change Request';
                  } else {
                    displayLabel = item.priorityType;
                  }
                } else if (item.priorityType === 'Deal Approval') {
                  displayLabel = 'Approve Closed Deal';
                } else if (item.priorityType === 'Approve UC') {
                  displayLabel = 'Approve Under Consideration';
                } else if (item.priorityType === 'Approve CCR') {
                  displayLabel = 'Approve Customer Change Request';
                } else {
                  displayLabel = item.priorityTitle || item.priorityType;
                }

                const projectId = item.projectId || item.items?.[0]?.project_id || filters.projectID;
                const projectName = item.projectName || item.items?.[0]?.project?.name;

                return (
                  <div
                    key={`approval-${
                      hasProjectFilter ? item.priorityType : `${item.priorityType}-${projectId}`
                    }-${index}`}
                    className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                    onClick={() => onApprovalClick(item, projectId)}>
                    <div className="flex flex-col gap-1 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-semibold">
                          {displayLabel.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-gray-800">
                          {displayLabel} | {item.count} {item.count === 1 ? 'task' : 'tasks'}
                        </span>
                      </div>
                      {!hasProjectFilter && projectName && (
                        <div className="ml-8 mt-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                            {projectName}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 px-3">
                <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">No approvals pending</p>
                <p className="text-xs text-gray-500 mt-1">
                  You are all caught up for now.
                </p>
              </div>
            )}
          </div>
        )}

        {actionCenterTab === 'escalations' && (
          <div className="mb-6 p-1">
            {hasEscalations ? (
              <>
                {salesEscalations && getSalesEscalationsCount(salesEscalations?.data) > 0 && (
                  <div
                    className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                    onClick={() => onEscalationClick('salesEscalations')}>
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-semibold">
                        E
                      </div>
                      <span className="text-sm font-medium text-gray-800">
                        Sales Escalations | {getSalesEscalationsCount(salesEscalations?.data)}{' '}
                        {getSalesEscalationsCount(salesEscalations?.data) === 1 ? 'task' : 'tasks'}
                      </span>
                    </div>
                  </div>
                )}

                {engineerEscalations && getEngineerEscalationsCount(engineerEscalations?.data) > 0 && (
                  <div
                    className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                    onClick={() => onEscalationClick('engineerEscalations')}>
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-semibold">
                        E
                      </div>
                      <span className="text-sm font-medium text-gray-800">
                        Engineer Escalations | {getEngineerEscalationsCount(engineerEscalations?.data)}{' '}
                        {getEngineerEscalationsCount(engineerEscalations?.data) === 1
                          ? 'task'
                          : 'tasks'}
                      </span>
                    </div>
                  </div>
                )}

                {inventoryTasks && getInventoryEscalationsCount(inventoryTasks) > 0 && (
                  <div
                    className="flex items-center justify-between border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                    onClick={() => onEscalationClick('inventoryEscalations')}>
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-semibold">
                        E
                      </div>
                      <span className="text-sm font-medium text-gray-800">
                        Inventory Escalations | {getInventoryEscalationsCount(inventoryTasks)}{' '}
                        {getInventoryEscalationsCount(inventoryTasks) === 1 ? 'task' : 'tasks'}
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 px-3">
                <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">No escalations right now</p>
                <p className="text-xs text-gray-500 mt-1">Everything looks stable at the moment.</p>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ActionCenterPanel;
